/**
 * Builds the same referral progress bundle as AdsWatchedStreak.fetchReferralsProgress,
 * using data already loaded on Home (direct list, streak completers, levels, per-ref streak).
 */

const DAILY_TARGET = 5;

function normalizeReferralProgressFromApiRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const uid = raw.userId ?? raw.UserID ?? raw.userID;
  if (uid == null) return null;
  const userIdNum = Number(uid);
  if (!Number.isFinite(userIdNum)) return null;
  const fromStreak = normalizeStreakMini(raw);
  if (fromStreak) {
    return {
      userId: userIdNum,
      adsWatchedToday: fromStreak.adsWatchedToday,
      currentStreak: fromStreak.currentStreak,
      longestStreak: fromStreak.longestStreak,
      goalPerDay: fromStreak.goalPerDay,
    };
  }
  return {
    userId: userIdNum,
    adsWatchedToday: Number(raw.adsWatchedToday ?? raw.AdsWatchedToday ?? raw.adsToday ?? 0) || 0,
    currentStreak: Number(raw.currentStreak ?? raw.CurrentStreak ?? 0) || 0,
    longestStreak: Number(raw.longestStreak ?? raw.LongestStreak ?? 0) || 0,
    goalPerDay: Number(raw.goalPerDay ?? raw.GoalPerDay ?? raw.dailyGoal ?? DAILY_TARGET) || DAILY_TARGET,
  };
}

function normalizeStreakMini(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  return {
    adsWatchedToday: Number(
      r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0
    ),
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? DAILY_TARGET),
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0),
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0),
  };
}

/**
 * @param {object} params
 * @param {object} params.listData - JSON from originalReferrals(userId)
 * @param {object} params.streakCompletersData - JSON from directReferralsStreakCompleters
 * @param {object} params.levelData - JSON from getUsersByLevel
 * @param {Map<number, object>} params.refStreakByUserId - normalizeStreakPayloadFull per direct ref
 * @returns {{ directReferrals: array, referralLevelByUserId: object, referralDesignationByUserId: object, referralsProgress: object }}
 */
export function buildReferralsActivitySnapshot({
  listData,
  streakCompletersData,
  levelData,
  refStreakByUserId,
}) {
  const levelMap = {};
  const designationMap = {};
  if (levelData?.success && Array.isArray(levelData.users)) {
    levelData.users.forEach((u) => {
      if (u?.UserID == null) return;
      const id = Number(u.UserID);
      levelMap[id] = u.Level;
      const dn = String(u?.DesignationName ?? u?.designationName ?? '').trim();
      if (dn && dn.toLowerCase() !== 'null') designationMap[id] = dn;
    });
  }

  const directList = Array.isArray(listData?.referrals) ? listData.referrals : [];

  const rawCompleters = streakCompletersData?.referrals ?? streakCompletersData?.Referrals ?? [];
  const completerList = Array.isArray(rawCompleters) ? rawCompleters : [];
  const byRefId = new Map();
  for (const row of completerList) {
    const n = normalizeReferralProgressFromApiRow(row);
    if (n) byRefId.set(n.userId, n);
  }

  const missingIds = directList
    .map((ref) => Number(ref.UserID))
    .filter((id) => Number.isFinite(id) && !byRefId.has(id));

  for (const refUserId of missingIds) {
    const norm = refStreakByUserId?.get(refUserId);
    if (!norm) continue;
    byRefId.set(refUserId, {
      userId: refUserId,
      adsWatchedToday: norm.adsWatchedToday,
      currentStreak: norm.currentStreak,
      longestStreak: norm.longestStreak,
      goalPerDay: norm.goalPerDay,
    });
  }

  const mergedReferrals = directList
    .map((ref) => {
      const id = Number(ref.UserID);
      if (!Number.isFinite(id)) return null;
      return (
        byRefId.get(id) ?? {
          userId: id,
          adsWatchedToday: 0,
          currentStreak: 0,
          longestStreak: 0,
          goalPerDay: DAILY_TARGET,
        }
      );
    })
    .filter(Boolean);

  return {
    directReferrals: directList,
    referralLevelByUserId: levelMap,
    referralDesignationByUserId: designationMap,
    referralsProgress: { ...streakCompletersData, referrals: mergedReferrals },
  };
}
